import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import type { DevanningPlan, DevanningPlanItem } from '../types'
import { fmtDate } from '../types'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from '../useColumnPrefs'
import { SortableColHeader, ColumnToggleButton, computeAutoFitWidths } from '../ColDragHeader'
import { useCustomersByRole } from '../../../hooks/useCustomersByRole'
import CustomerCombobox from '../../../components/CustomerCombobox'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { usePagination } from '../../../hooks/usePagination'
import PaginationControls from '../../../components/PaginationControls'
import { classifyOverdue, OVERDUE_ROW_CLASS, OVERDUE_TEXT_CLASS, overdueBadgeText } from '../../../lib/overdue'

const PLAN_COL_DEFS = [
  { id: 'job_ref',       label: 'Job Ref' },
  { id: 'container',     label: 'Container' },
  { id: 'vessel',        label: 'Vessel' },
  { id: 'customer',      label: 'Customer' },
  { id: 'eta',           label: 'ETA' },
  { id: 'free_days',     label: 'Free Days' },
  { id: 'free_date',     label: 'Free Date' },
  { id: 'planned_devan_date', label: 'Planned Devan Date' },
  { id: 'planned_devan_time', label: 'Time' },
  { id: 'ctns',          label: 'CTNS' },
  { id: 'created',       label: 'Created' },
] as const

const PLANS_AUTO_WIDTHS = computeAutoFitWidths(PLAN_COL_DEFS)

interface PlanDeleteInfo {
  plan: PlanRow
  devanningCount: number  // >0 means blocked
}


interface PlanRow extends DevanningPlan {
  ctns: number
  devanningId: string | null
  source_ll?: {
    job_ref: string | null
    container_eta_at: string | null
    free_demurrage_days: number | null
    source_customer?: { name: string } | null
  } | null
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spin() {
  return (
    <svg className="animate-spin w-3 h-3 inline text-blue-500" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

// ─── Inline edit helpers ───────────────────────────────────────────────────────

const cellInputCls = 'w-full border border-blue-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white'

function EditText({ value, onCommit, onCancel }: {
  value: string
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.select() }, [])
  return (
    <input ref={ref} className={cellInputCls} value={v} onChange={e => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }}
    />
  )
}

function EditDate({ value, onCommit, onCancel }: {
  value: string
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return (
    <input ref={ref} type="date" className={cellInputCls} value={v} onChange={e => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }}
    />
  )
}

function EditNumber({ value, onCommit, onCancel, min = 1 }: {
  value: string
  onCommit: (v: string) => void
  onCancel: () => void
  min?: number
}) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.select() }, [])
  return (
    <input ref={ref} type="number" min={min} className={`${cellInputCls} w-16`} value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }}
    />
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPDDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtPDTime(iso: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return t === '00:00' ? '—' : t
}

function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ─── Plan Dialog (#163) ────────────────────────────────────────────────────────
// Moves a pending plan to Planned state: sets planned_devan_at (required),
// marks plan.status='planned', creates devanning record + copies plan items.

function PlanDialog({ plan, warehouseId, onDone, onClose }: {
  plan: PlanRow
  warehouseId: string
  onDone: (devanningId: string) => void
  onClose: () => void
}) {
  const [plannedAt, setPlannedAt] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!plannedAt) { setError('Please set planned devan date before moving to Devannings.'); return }
    setSaving(true); setError(null)
    try {
      const iso = new Date(plannedAt).toISOString()
      const { error: planErr } = await supabase.from('devanning_plans').update({
        status: 'planned', planned_devan_at: iso,
      }).eq('id', plan.id)
      if (planErr) throw planErr

      // UPSERT devanning (idempotent — Generate Plan may have pre-created it)
      const { data: existingDev } = await supabase
        .from('devannings').select('id').eq('plan_id', plan.id).maybeSingle()
      let devId: string
      if (existingDev) {
        devId = existingDev.id
      } else {
        const { data: newDev, error: devErr } = await supabase.from('devannings').insert({
          plan_id: plan.id, warehouse_id: warehouseId,
          container_no: plan.container_no, status: 'awaiting',
        }).select('id').single()
        if (devErr) throw devErr
        devId = newDev.id
      }

      // Replace devanning_items from plan_items (always fresh sync)
      const { data: planItems } = await supabase.from('devanning_plan_items')
        .select('*').eq('plan_id', plan.id).order('sort_order')
      if (planItems && planItems.length > 0) {
        await supabase.from('devanning_items').delete().eq('devanning_id', devId)
        await supabase.from('devanning_items').insert(
          planItems.map((pi: DevanningPlanItem) => ({
            devanning_id: devId, plan_item_id: pi.id, hbl_no: pi.hbl_no,
            description: pi.description ?? null, customer_id: pi.customer_id,
            delivery_address: pi.delivery_address, delivery_method: pi.delivery_method ?? null,
            expected_cartons: pi.expected_cartons, expected_weight_kg: pi.expected_weight_kg,
            expected_cbm: pi.expected_cbm, actual_cartons: 0, pallet_count: 0,
            pallets_qty: 0, pallets_detail: [], condition: 'good',
            split_no: pi.split_no ?? null, system_ref: pi.system_ref ?? null,
            group_name: pi.group_name ?? null,
          }))
        )
      }
      onDone(devId)
    } catch (err: any) {
      setError(err.message || 'Failed')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Schedule Devanning</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This plan will move to <strong>Planned</strong> status and appear in both the Plans tab and the Devannings tab.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Planned Devan Date &amp; Time <span className="text-red-500">*</span>
            </label>
            <input type="datetime-local"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={plannedAt} onChange={e => setPlannedAt(e.target.value)} />
          </div>
          <div className="text-xs text-gray-400">Container: <span className="font-mono font-semibold text-gray-600 dark:text-gray-300">{plan.container_no || '(empty)'}</span></div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button onClick={handleConfirm} disabled={saving || !plannedAt}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Moving…' : 'Confirm → Move to Devannings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PlansPage() {
  const { id } = useParams<{ id: string }>()
  const { userEmail, user } = useAuth()
  const [plans, setPlans] = useState<PlanRow[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'planned'>('all')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [planDeleteInfo, setPlanDeleteInfo] = useState<PlanDeleteInfo | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Plan dialog (#163)
  const [planDialog, setPlanDialog] = useState<PlanRow | null>(null)

  // Inline pending cell → move to planned
  const [inlinePlanCell, setInlinePlanCell] = useState<{ planId: string; value: string } | null>(null)
  const [inlinePlanSaving, setInlinePlanSaving] = useState<string | null>(null)

  // Reschedule planned → dialog with reason
  const [rescheduleDialog, setRescheduleDialog] = useState<{ plan: PlanRow } | null>(null)
  const [rescheduleAt, setRescheduleAt] = useState('')
  const [rescheduleReason, setRescheduleReason] = useState('')
  const [rescheduleSaving, setRescheduleSaving] = useState(false)

  // Cancel planned → revert to pending
  const [cancelDialog, setCancelDialog] = useState<PlanRow | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelSaving, setCancelSaving] = useState(false)

  // Inline edit state
  type EditField = 'container_no' | 'vessel' | 'source_customer_id' | 'eta_date' | 'free_demurrage_days'
  const [editCell, setEditCell] = useState<{ planId: string; field: EditField; value: string } | null>(null)
  const [savingCell, setSavingCell] = useState<string | null>(null)
  const { customers } = useCustomersByRole('Cus')

  // Column prefs
  const { order: colOrder, hidden: colHidden, visibleCols, setOrder: setColOrder, toggleHidden, reset: resetCols,
    extra: plansExtra, setExtra: setPlansExtra } =
    useColumnPrefs('col_pref_plans', PLAN_COL_DEFS, user?.id, { dbKey: 'col_order_plans' }, { widths: {} })
  const plansSavedWidths = (plansExtra.widths as Record<string, number>) ?? {}
  const getPlanColW = (id: string) => plansSavedWidths[id] ?? PLANS_AUTO_WIDTHS[id] ?? 120
  const savePlanColW = (id: string, w: number) => setPlansExtra({ ...plansExtra, widths: { ...plansSavedWidths, [id]: w } })
  const resetPlanColW = (id: string) => { const n = { ...plansSavedWidths }; delete n[id]; setPlansExtra({ ...plansExtra, widths: n }) }
  const planSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handlePlanColDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const oi = colOrder.indexOf(active.id as string)
    const ni = colOrder.indexOf(over.id as string)
    if (oi !== -1 && ni !== -1) setColOrder(arrayMove(colOrder, oi, ni))
  }

  useEffect(() => {
    if (!id) { setLoading(false); return }
    setLoadError(null)
    async function load() {
      try {
        // No "source_loading_list_id IS NOT NULL" filter here (#271) — that filter was
        // added for #235/#236 to hide zombie schedule-only plan rows, but nothing has
        // created a plan row without a loading list since the one-time cleanup in
        // migration 20260628_devanning_schedule_table.sql. A plan CAN still end up with
        // source_loading_list_id = NULL today if its loading list is deleted later
        // (devanning_plans_source_loading_list_id_fkey is ON DELETE SET NULL, by design,
        // so deleting an LL never cascade-deletes the plan). Filtering those out here
        // hid real pending/planned plans (e.g. #271: KI18083M) even though the table
        // already has a "noLL" warning UI built specifically for this case below.
        const { data, error } = await supabase.from('devanning_plans')
          .select('*, devanning_plan_items(expected_cartons), source_ll:loading_lists!source_loading_list_id(job_ref, container_eta_at, free_demurrage_days, source_customer:customers!source_customer_id(name)), devannings(id, status)')
          .eq('warehouse_id', id)
          .in('status', ['pending', 'planned'])
          .order('created_at', { ascending: false })
        if (error) { setLoadError(error.message); setLoading(false); return }
        const rows = (data || []).map((p: any) => {
          const devArr: any[] = Array.isArray(p.devannings) ? p.devannings : (p.devannings ? [p.devannings] : [])
          const activeDev = devArr.find((d: any) => d.status !== 'cancelled') ?? devArr[0] ?? null
          return {
            ...p,
            ctns: (p.devanning_plan_items || []).reduce((s: number, it: any) => s + (it.expected_cartons || 0), 0),
            devanningId: activeDev?.id ?? null,
            source_ll: Array.isArray(p.source_ll) ? p.source_ll[0] ?? null : p.source_ll ?? null,
          }
        }) as PlanRow[]
        setPlans(rows)
        setLoading(false)
      } catch (err: any) {
        console.error('Plans load error:', err)
        setLoadError(err.message || 'Load failed')
        setLoading(false)
      }
    }
    load()
  }, [id])

  const startEdit = (planId: string, field: EditField, value: string) => {
    setEditCell({ planId, field, value })
  }

  const cancelEdit = () => setEditCell(null)

  const saveCell = useCallback(async (plan: PlanRow, field: EditField, rawValue: string) => {
    const key = `${plan.id}:${field}`
    setSavingCell(key)
    setEditCell(null)
    try {
      if (field === 'container_no' || field === 'vessel' || field === 'eta_date') {
        const { error } = await supabase.from('devanning_plans').update({ [field]: rawValue || null }).eq('id', plan.id)
        if (error) throw error
        setPlans(prev => prev.map(p => {
          if (p.id !== plan.id) return p
          if (field === 'container_no') return { ...p, container_no: rawValue }
          if (field === 'vessel') return { ...p, vessel: rawValue || null }
          if (field === 'eta_date') return { ...p, eta_date: rawValue || null }
          return p
        }))
      } else if (field === 'source_customer_id' || field === 'free_demurrage_days') {
        if (!plan.source_loading_list_id) return
        const patchVal = field === 'free_demurrage_days' ? (parseInt(rawValue) || 5) : (rawValue || null)
        const { error } = await supabase.from('loading_lists').update({ [field]: patchVal }).eq('id', plan.source_loading_list_id)
        if (error) throw error
        setPlans(prev => prev.map(p => {
          if (p.id !== plan.id || !p.source_ll) return p
          if (field === 'source_customer_id') {
            const cust = customers.find(c => c.id === rawValue)
            return { ...p, source_ll: { ...p.source_ll!, source_customer: cust ? { name: cust.name } : null } }
          }
          if (field === 'free_demurrage_days') {
            return { ...p, source_ll: { ...p.source_ll!, free_demurrage_days: parseInt(rawValue) || 5 } }
          }
          return p
        }))
      }
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSavingCell(null)
    }
  }, [customers])

  const handleDeleteClick = async (plan: PlanRow) => {
    const { count } = await supabase.from('devannings')
      .select('id', { count: 'exact', head: true }).eq('plan_id', plan.id)
    setPlanDeleteInfo({ plan, devanningCount: count || 0 })
  }

  const handleDeleteConfirm = async () => {
    if (!planDeleteInfo || planDeleteInfo.devanningCount > 0) return
    const { plan } = planDeleteInfo
    setDeleting(true)
    try {
      await supabase.from('devanning_plan_items').delete().eq('plan_id', plan.id)
      await supabase.from('devanning_plans').delete().eq('id', plan.id)
      setPlans(prev => prev.filter(x => x.id !== plan.id))
      setPlanDeleteInfo(null)
    } finally {
      setDeleting(false)
    }
  }

  const handleMoveToPlan = async (plan: PlanRow, plannedAt: string) => {
    if (!plannedAt) return
    setInlinePlanSaving(plan.id)
    setInlinePlanCell(null)
    try {
      const iso = new Date(plannedAt).toISOString()
      const { error: planErr } = await supabase.from('devanning_plans').update({
        status: 'planned', planned_devan_at: iso,
      }).eq('id', plan.id)
      if (planErr) throw planErr

      // UPSERT devanning (idempotent — Generate Plan may have pre-created it)
      const { data: existingDevInline } = await supabase
        .from('devannings').select('id').eq('plan_id', plan.id).maybeSingle()
      let inlineDevId: string
      if (existingDevInline) {
        inlineDevId = existingDevInline.id
      } else {
        const { data: newDev, error: devErr } = await supabase.from('devannings').insert({
          plan_id: plan.id, warehouse_id: id, container_no: plan.container_no,
          status: 'awaiting',
        }).select('id').single()
        if (devErr) throw devErr
        inlineDevId = newDev.id
      }

      // Replace devanning_items from plan_items (always fresh sync)
      const { data: planItems } = await supabase.from('devanning_plan_items')
        .select('*').eq('plan_id', plan.id).order('sort_order')
      if (planItems && planItems.length > 0) {
        await supabase.from('devanning_items').delete().eq('devanning_id', inlineDevId)
        await supabase.from('devanning_items').insert(
          planItems.map((pi: DevanningPlanItem) => ({
            devanning_id: inlineDevId, plan_item_id: pi.id, hbl_no: pi.hbl_no,
            description: pi.description ?? null, customer_id: pi.customer_id,
            delivery_address: pi.delivery_address, delivery_method: pi.delivery_method ?? null,
            expected_cartons: pi.expected_cartons, expected_weight_kg: pi.expected_weight_kg,
            expected_cbm: pi.expected_cbm, actual_cartons: 0, pallet_count: 0,
            pallets_qty: 0, pallets_detail: [], condition: 'good',
            split_no: pi.split_no ?? null, system_ref: pi.system_ref ?? null,
            group_name: pi.group_name ?? null,
          }))
        )
      }
      setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, status: 'planned', planned_devan_at: iso, devanningId: inlineDevId } : p))
    } catch (err: any) {
      alert(err.message)
    } finally {
      setInlinePlanSaving(null)
    }
  }

  const handleReschedule = async () => {
    if (!rescheduleDialog || !rescheduleAt || rescheduleReason.trim().length < 5) return
    setRescheduleSaving(true)
    try {
      const iso = new Date(rescheduleAt).toISOString()
      const now = new Date().toISOString()
      const actor = userEmail || 'unknown'
      const { error: planErr } = await supabase.from('devanning_plans').update({
        planned_devan_at: iso, last_reason: rescheduleReason, last_changed_at: now, last_changed_by: actor,
      }).eq('id', rescheduleDialog.plan.id)
      if (planErr) throw planErr
      if (rescheduleDialog.plan.devanningId) {
        await supabase.from('devannings').update({ last_reason: rescheduleReason, last_changed_at: now, last_changed_by: actor }).eq('id', rescheduleDialog.plan.devanningId)
      }
      setPlans(prev => prev.map(p => p.id === rescheduleDialog.plan.id ? { ...p, planned_devan_at: iso } : p))
      setRescheduleDialog(null)
      setRescheduleAt('')
      setRescheduleReason('')
    } catch (err: any) {
      alert(err.message)
    } finally {
      setRescheduleSaving(false)
    }
  }

  const handleCancelPlan = async () => {
    if (!cancelDialog || cancelReason.trim().length < 5) return
    setCancelSaving(true)
    try {
      const now = new Date().toISOString()
      const actor = userEmail || 'unknown'
      const { error: planErr } = await supabase.from('devanning_plans').update({
        status: 'pending', planned_devan_at: null,
        last_reason: cancelReason, last_changed_at: now, last_changed_by: actor,
      }).eq('id', cancelDialog.id)
      if (planErr) throw planErr
      if (cancelDialog.devanningId) {
        await supabase.from('devanning_items').delete().eq('devanning_id', cancelDialog.devanningId)
        await supabase.from('devannings').delete().eq('id', cancelDialog.devanningId)
      }
      setPlans(prev => prev.map(p => p.id === cancelDialog.id ? { ...p, status: 'pending', planned_devan_at: null, devanningId: null } : p))
      setCancelDialog(null)
      setCancelReason('')
    } catch (err: any) {
      alert(err.message)
    } finally {
      setCancelSaving(false)
    }
  }

  const visible = useMemo(() => {
    let result = statusFilter === 'all' ? plans : statusFilter === 'pending' ? plans.filter(p => !p.planned_devan_at) : plans.filter(p => !!p.planned_devan_at)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(p =>
        (p.source_ll?.job_ref || '').toLowerCase().includes(q) ||
        (p.container_no || '').toLowerCase().includes(q) ||
        (p.vessel || '').toLowerCase().includes(q) ||
        (p.source_ll?.source_customer?.name || '').toLowerCase().includes(q)
      )
    }
    return result
  }, [plans, search, statusFilter])

  const { scrollProps: plansScrollProps, contentRef: plansTableRef, bar: plansScrollBar } = useStickyHScroll([visible, visibleCols])

  const { page, setPage, pageSize, setPageSize } = usePagination('plans-list')
  useEffect(() => { setPage(0) }, [search, statusFilter]) // eslint-disable-line react-hooks/exhaustive-deps
  const pagedTotal = visible.length
  const paged = pageSize === 'all' ? visible : visible.slice(page * pageSize, page * pageSize + pageSize)

  // Editable cell wrapper: hover → show pencil, click → open edit
  const tdEdit = 'group cursor-text select-none'
  const editIcon = (
    <svg className="inline-block ml-1 w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" />
    </svg>
  )

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">Pre-Devanning Plans</h2>
              <p className="text-xs text-gray-400 mt-0.5">Pending and planned — click Open to edit splits/items, Plan → to schedule</p>
            </div>
            {/* Status filter tabs */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 gap-0.5">
              {(['all', 'pending', 'planned'] as const).map(f => {
                const count = f === 'all' ? plans.length : f === 'pending' ? plans.filter(p => !p.planned_devan_at).length : plans.filter(p => !!p.planned_devan_at).length
                return (
                  <button key={f} onClick={() => setStatusFilter(f)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${statusFilter === f ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
                    {f === 'all' ? 'All' : f === 'pending' ? 'Pending' : 'Planned'} ({count})
                  </button>
                )
              })}
            </div>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search job ref, container, vessel, customer..."
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <ColumnToggleButton cols={PLAN_COL_DEFS} hidden={colHidden} onToggle={toggleHidden} onReset={resetCols}
              onRecalculate={() => { const w = computeAutoFitWidths(PLAN_COL_DEFS); setPlansExtra({ ...plansExtra, widths: { ...w, ...plansSavedWidths } }) }} />
          </div>
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading...</div>
        ) : loadError ? (
          <div className="py-16 text-center text-red-500 text-sm px-4">Error loading plans: {loadError}</div>
        ) : (
          <div className="overflow-x-auto" {...plansScrollProps}>
            <table ref={plansTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
              <DndContext sensors={planSensors} collisionDetection={closestCenter} onDragEnd={handlePlanColDragEnd}>
                <SortableContext items={visibleCols.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {visibleCols.map(col => (
                        <SortableColHeader key={col.id} id={col.id}
                          style={{ width: getPlanColW(col.id), minWidth: '50px' }}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                          onResizeEnd={w => savePlanColW(col.id, w)}
                          onResizeReset={() => resetPlanColW(col.id)}>
                          {col.label}
                        </SortableColHeader>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap" />
                    </tr>
                  </thead>
                </SortableContext>
              </DndContext>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paged.map(p => {
                  const etaStr = p.eta_date || p.source_ll?.container_eta_at?.slice(0, 10) || null
                  const etaDate = etaStr ? new Date(etaStr) : null
                  const freeDays = p.source_ll?.free_demurrage_days ?? 5
                  const freeDate = etaDate ? new Date(etaDate.getTime() + freeDays * 86400000) : null
                  const today = new Date(); today.setHours(0, 0, 0, 0)
                  const daysLeft = freeDate ? Math.ceil((freeDate.getTime() - today.getTime()) / 86400000) : null
                  // classifyOverdue works in "days past due" terms, the inverse of daysLeft.
                  const daysPastFree = daysLeft !== null ? -daysLeft : null
                  const freeLevel = classifyOverdue(daysPastFree, { warnAtDays: -3, dangerAtDays: 1 }, p.status === 'pending')
                  const overdue = freeLevel === 'danger'
                  const nearFree = freeLevel === 'warn'
                  const noLL   = !p.source_loading_list_id && !!p.job_ref
                  const rowCls = freeLevel !== 'ok'
                    ? OVERDUE_ROW_CLASS[freeLevel]
                    : noLL
                      ? 'bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'

                  const isEditing = (f: EditField) => editCell?.planId === p.id && editCell.field === f
                  const isSaving = (f: EditField) => savingCell === `${p.id}:${f}`

                  const effectiveJobRef = p.source_ll?.job_ref || p.job_ref || null
                  const cells: Record<string, React.ReactNode> = {
                    job_ref: (
                      <td key="job_ref" className="px-4 py-3 font-mono font-semibold text-blue-700 dark:text-blue-400 whitespace-nowrap">
                        {effectiveJobRef
                          ? <span className="flex items-center gap-1.5">
                              {effectiveJobRef}
                              {noLL && <span title="Loading List not uploaded" className="text-amber-500 cursor-default">⚠</span>}
                            </span>
                          : '—'}
                      </td>
                    ),
                    container:     <td key="container" className={`px-4 py-3 font-mono font-semibold text-gray-900 dark:text-white ${tdEdit}`} onClick={() => !isEditing('container_no') && !isSaving('container_no') && startEdit(p.id, 'container_no', p.container_no || '')}>{isSaving('container_no') ? <Spin /> : isEditing('container_no') ? <EditText value={editCell!.value} onCommit={v => saveCell(p, 'container_no', v)} onCancel={cancelEdit} /> : <span>{p.container_no || '—'}{editIcon}</span>}</td>,
                    vessel:        <td key="vessel" className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${tdEdit}`} onClick={() => !isEditing('vessel') && !isSaving('vessel') && startEdit(p.id, 'vessel', p.vessel || '')}>{isSaving('vessel') ? <Spin /> : isEditing('vessel') ? <EditText value={editCell!.value} onCommit={v => saveCell(p, 'vessel', v)} onCancel={cancelEdit} /> : <span>{p.vessel || '—'}{editIcon}</span>}</td>,
                    customer:      <td key="customer" className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${tdEdit}`} onClick={() => !isEditing('source_customer_id') && !isSaving('source_customer_id') && p.source_loading_list_id && startEdit(p.id, 'source_customer_id', '')}>{isSaving('source_customer_id') ? <Spin /> : isEditing('source_customer_id') ? <CustomerCombobox value={editCell!.value} onChange={v => saveCell(p, 'source_customer_id', v)} onClose={cancelEdit} /> : <span>{p.source_ll?.source_customer?.name || '—'}{p.source_loading_list_id && editIcon}</span>}</td>,
                    eta:           <td key="eta" className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${tdEdit}`} onClick={() => !isEditing('eta_date') && !isSaving('eta_date') && startEdit(p.id, 'eta_date', p.eta_date || '')}>{isSaving('eta_date') ? <Spin /> : isEditing('eta_date') ? <EditDate value={editCell!.value} onCommit={v => saveCell(p, 'eta_date', v)} onCancel={cancelEdit} /> : <span>{fmtDate(p.eta_date)}{editIcon}</span>}</td>,
                    free_days:     <td key="free_days" className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${tdEdit}`} onClick={() => !isEditing('free_demurrage_days') && !isSaving('free_demurrage_days') && p.source_loading_list_id && startEdit(p.id, 'free_demurrage_days', String(p.source_ll?.free_demurrage_days ?? 5))}>{isSaving('free_demurrage_days') ? <Spin /> : isEditing('free_demurrage_days') ? <EditNumber value={editCell!.value} onCommit={v => saveCell(p, 'free_demurrage_days', v)} onCancel={cancelEdit} /> : <span className="font-mono">{p.source_ll?.free_demurrage_days ?? 5}{p.source_loading_list_id && editIcon}</span>}</td>,
                    free_date:     <td key="free_date" className="px-4 py-3 text-xs">{freeDate ? <span className={OVERDUE_TEXT_CLASS[freeLevel]} title={overdue ? `${Math.abs(daysLeft!)} days over free period` : nearFree ? `${daysLeft} days to free-period deadline` : undefined}>{freeDate.toLocaleDateString('en-GB')}{freeLevel !== 'ok' && ` ${overdueBadgeText(daysPastFree!, freeLevel)}`}</span> : '—'}</td>,
                    planned_devan_date: <td key="planned_devan_date" className="group px-4 py-3 text-xs whitespace-nowrap cursor-pointer" onClick={() => { if (p.status === 'pending' && inlinePlanSaving !== p.id) { setInlinePlanCell({ planId: p.id, value: p.planned_devan_at ? toLocalDateTimeInput(p.planned_devan_at) : '' }) } else if (p.status === 'planned') { setRescheduleDialog({ plan: p }); setRescheduleAt(p.planned_devan_at ? toLocalDateTimeInput(p.planned_devan_at) : ''); setRescheduleReason('') } }}>{inlinePlanSaving === p.id ? <Spin /> : inlinePlanCell?.planId === p.id ? <input autoFocus type="datetime-local" className="border border-blue-400 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white" value={inlinePlanCell.value} onChange={e => setInlinePlanCell({ planId: p.id, value: e.target.value })} onBlur={() => { if (inlinePlanCell.value) handleMoveToPlan(p, inlinePlanCell.value); else setInlinePlanCell(null) }} onKeyDown={e => { if (e.key === 'Enter' && inlinePlanCell.value) handleMoveToPlan(p, inlinePlanCell.value); if (e.key === 'Escape') setInlinePlanCell(null) }} onClick={e => e.stopPropagation()} /> : p.status === 'pending' && !p.planned_devan_at ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 select-none">pending — click to plan</span> : <span className="flex items-center gap-1 text-blue-700 dark:text-blue-400">{fmtPDDate(p.planned_devan_at)}<svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg></span>}</td>,
                    planned_devan_time: <td key="planned_devan_time" className="group px-4 py-3 font-mono text-xs whitespace-nowrap cursor-pointer text-gray-600 dark:text-gray-300" onClick={() => { if (p.status === 'pending' && inlinePlanSaving !== p.id) { setInlinePlanCell({ planId: p.id, value: p.planned_devan_at ? toLocalDateTimeInput(p.planned_devan_at) : '' }) } else if (p.status === 'planned') { setRescheduleDialog({ plan: p }); setRescheduleAt(p.planned_devan_at ? toLocalDateTimeInput(p.planned_devan_at) : ''); setRescheduleReason('') } }}>{inlinePlanSaving === p.id ? <Spin /> : fmtPDTime(p.planned_devan_at)}</td>,
                    ctns:          <td key="ctns" className="px-4 py-3 font-mono font-semibold text-gray-800 dark:text-gray-100">{p.ctns || '—'}</td>,
                    created:       <td key="created" className="px-4 py-3 text-gray-400 text-xs">{fmtDate(p.created_at)}</td>,
                  }

                  return (
                    <tr key={p.id} className={rowCls}>
                      {visibleCols.map(col => cells[col.id])}
                      <td className="px-4 py-3 whitespace-nowrap space-x-2 text-xs">
                        {noLL && (<Link to={`/warehouse/${id}/loading-lists/upload?job_ref=${encodeURIComponent(p.job_ref || '')}&container_no=${encodeURIComponent(p.container_no || '')}`} className="text-amber-600 hover:text-amber-800 font-semibold hover:underline">Upload LL</Link>)}
                        {p.status === 'pending' && (<button onClick={() => setPlanDialog(p)} className="text-green-600 hover:text-green-800 font-semibold hover:underline">Plan</button>)}
                        {p.status === 'planned' && (<button onClick={() => { setRescheduleDialog({ plan: p }); setRescheduleAt(p.planned_devan_at ? toLocalDateTimeInput(p.planned_devan_at) : ''); setRescheduleReason('') }} className="text-blue-600 hover:underline">Reschedule</button>)}
                        <Link to={`/warehouse/${id}/plans/${p.id}`} className="text-blue-600 hover:underline">Open</Link>
                        {p.status === 'planned' && p.devanningId && (<Link to={`/warehouse/${id}/devannings/${p.devanningId}`} className="text-indigo-600 hover:underline">Preview Workflow</Link>)}
                        {p.status === 'pending' && (<button onClick={() => handleDeleteClick(p)} className="text-red-500 hover:text-red-700 hover:underline">Delete</button>)}
                        {p.status === 'planned' && (<button onClick={() => { setCancelDialog(p); setCancelReason('') }} className="text-red-500 hover:text-red-700 hover:underline">Cancel</button>)}
                      </td>
                    </tr>
                  )
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-gray-400">
                    {search ? 'No results for this search.' : statusFilter === 'all' ? 'No plans found.' : `No ${statusFilter} plans.`}
                  </td></tr>
                )}

              </tbody>
            </table>
          </div>
        )}
        {!loading && visible.length > 0 && (
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={pagedTotal} />
        )}
      </div>
      <StickyHScrollbar {...plansScrollBar} />
      {planDeleteInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            {planDeleteInfo.devanningCount > 0 ? (
              <>
                <h3 className="font-semibold text-amber-600 dark:text-amber-400 mb-3">⚠ Cannot Delete Plan</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  &ldquo;<strong>{planDeleteInfo.plan.container_no}</strong>&rdquo;
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                  {planDeleteInfo.devanningCount} Devanning record{planDeleteInfo.devanningCount !== 1 ? 's' : ''} still exist{planDeleteInfo.devanningCount === 1 ? 's' : ''}.
                  Please delete {planDeleteInfo.devanningCount === 1 ? 'it' : 'them'} first, then come back.
                </p>
                <div className="flex justify-end">
                  <button onClick={() => setPlanDeleteInfo(null)}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Confirm Delete Plan</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  &ldquo;<strong>{planDeleteInfo.plan.container_no}</strong>&rdquo;
                </p>
                <p className="text-sm font-semibold text-red-600 mb-4">This will permanently remove the plan and all plan items.</p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setPlanDeleteInfo(null)} disabled={deleting}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={handleDeleteConfirm} disabled={deleting}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Confirm Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {planDialog && (
        <PlanDialog
          plan={planDialog}
          warehouseId={id!}
          onDone={(devanningId) => {
            setPlans(prev => prev.map(p => p.id === planDialog.id ? { ...p, status: 'planned', devanningId: devanningId ?? null } : p))
            setPlanDialog(null)
          }}
          onClose={() => setPlanDialog(null)}
        />
      )}

      {rescheduleDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-0.5">Reschedule Devanning</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 font-mono">{rescheduleDialog.plan.container_no}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Date &amp; Time <span className="text-red-500">*</span></label>
              <input type="datetime-local" value={rescheduleAt} onChange={e => setRescheduleAt(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason <span className="text-red-500">*</span> <span className="text-gray-400 font-normal text-xs">(min 5 chars)</span></label>
              <textarea rows={2} value={rescheduleReason} onChange={e => setRescheduleReason(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              {rescheduleReason.length > 0 && rescheduleReason.trim().length < 5 && <p className="mt-1 text-xs text-red-500">Reason must be at least 5 characters.</p>}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRescheduleDialog(null)} disabled={rescheduleSaving}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Cancel</button>
              <button onClick={handleReschedule} disabled={rescheduleSaving || !rescheduleAt || rescheduleReason.trim().length < 5}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {rescheduleSaving ? 'Saving…' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-0.5">Cancel Planned Devanning</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-mono">{cancelDialog.container_no}</p>
            <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 mb-4">
              This will delete the awaiting devanning and return the plan to Pending status.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason <span className="text-red-500">*</span> <span className="text-gray-400 font-normal text-xs">(min 5 chars)</span></label>
              <textarea rows={2} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              {cancelReason.length > 0 && cancelReason.trim().length < 5 && <p className="mt-1 text-xs text-red-500">Reason must be at least 5 characters.</p>}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCancelDialog(null)} disabled={cancelSaving}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Back</button>
              <button onClick={handleCancelPlan} disabled={cancelSaving || cancelReason.trim().length < 5}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {cancelSaving ? 'Cancelling…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
